import React from "react";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	evaluateTypeOrFunction,
	FormattedText,
	Grid,
	Heading,
	If,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	Rule,
	StyleProps,
	toGrammaticalNumber,
	Tooltip,
	TruncatedText,
	TypeOrFunction,
	useTimeout,
} from "@hex-insights/core";
import {
	Badge,
	Employee,
	EmployeeComponents,
	EmployeeNameAndImageFragment,
	EmptyView,
	HomeRoomSectionFormat,
	Icon,
	Student,
	StudentClassPlacementQuery,
	StudentComponents,
	StudentFormat,
	useParentPortalViewerInfoQuery,
	useStudentClassPlacementQuery,
	useStudentMailCredentialsQuery,
} from "@hex-insights/verita-romania.shared";
import styles from "./styles.module.css";

export type ClassPlacementModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

export function ClassPlacementModal({ ifRef, onClose, className, style }: ClassPlacementModalProps) {
	return (
		<Modal ifRef={ifRef} onClose={onClose} className={makeClassName(styles["modal"], className)} style={style}>
			<Modal.Header className={styles["modal__header"]}>
				<Heading.H2 noMargin>Class Placement</Heading.H2>
			</Modal.Header>
			<Modal.Body className={styles["modal__body"]}>
				<ClassPlacementBody style={{ flexGrow: 1 }} />
			</Modal.Body>
		</Modal>
	);
}

const singleStudentResponsiveColumns = {
	0: 1,
};

const responsiveTracks = {
	0: 1,
	400: 2,
};

export type ClassPlacementBodyProps = Partial<ClassNameProps & StyleProps>;

export function ClassPlacementBody({ className, style }: ClassPlacementBodyProps) {
	const { loading: loadingViewer, data: viewerData } = useParentPortalViewerInfoQuery();
	const viewer = viewerData?.viewer;
	// TODO only currently or future enrolled students (and same for onboarding form submission)
	const students = viewer?.person?.guardian?.relationships.map((e) => e.student) ?? [];

	const [selectedStudentID, setSelectedStudentID] = React.useState<Student["id"] | null>(null);
	React.useEffect(() => {
		if (!viewer || selectedStudentID !== null) {
			return;
		}
		setSelectedStudentID(viewer?.person?.guardian?.relationships[0]?.student?.id ?? null);
	}, [viewer, selectedStudentID]);

	const selectedStudent = viewer?.person?.guardian?.relationships.find(
		(e) => e.student.id === selectedStudentID,
	)?.student;

	return (
		<Column justify="spaced-start" verticalSpacing="1.5rem" className={className} style={style}>
			<Conditional>
				<If condition={loadingViewer}>Loading...</If>
				<Else>
					<Column justify="spaced-start">
						<Grid
							responsiveTracks={students.length === 1 ? singleStudentResponsiveColumns : responsiveTracks}
							gap="0.75rem"
						>
							{students.map((student) => (
								<Button
									key={student.id}
									onClick={() => setSelectedStudentID(student.id)}
									className={makeClassName(
										styles["student-button"],
										selectedStudentID === student.id ? styles["student-button--selected"] : "",
									)}
								>
									<Row justify="spaced-start" align="center">
										<StudentComponents.StudentIcon student={student} />
										<TruncatedText>{StudentFormat.name(student)}</TruncatedText>
									</Row>
								</Button>
							))}
						</Grid>
					</Column>
				</Else>
			</Conditional>

			{!!selectedStudent && <StudentClassPlacement student={selectedStudent} />}
		</Column>
	);
}

const teachersGridTracks = {
	0: 1,
	400: 2,
};

type StudentClassPlacementProps = {
	student: Pick<Student, "id"> & { person: Pick<Student["person"], "givenName"> };
};

function StudentClassPlacement({ student }: StudentClassPlacementProps) {
	const { loading, data } = useStudentClassPlacementQuery({ variables: { studentID: student.id } });

	const { homeRoomSections, teachers } = React.useMemo(() => {
		const homeRoomSections: StudentClassPlacementQuery["studentHomeRoomSectionEnrollmentConnection"]["edges"][0]["node"]["homeRoomSection"][] =
			[];
		const teachers: StudentClassPlacementQuery["studentHomeRoomSectionEnrollmentConnection"]["edges"][0]["node"]["homeRoomSection"]["teacherHomeRoomSectionEnrollments"][0]["teacher"][] =
			[];
		if (!data) {
			return { homeRoomSections, teachers };
		}

		for (let i = 0; i < data.studentHomeRoomSectionEnrollmentConnection.edges.length; i++) {
			const homeRoomSection = data.studentHomeRoomSectionEnrollmentConnection.edges[i].node.homeRoomSection;
			homeRoomSections.push(homeRoomSection);
			for (let j = 0; j < homeRoomSection.teacherHomeRoomSectionEnrollments.length; j++) {
				const teacher = homeRoomSection.teacherHomeRoomSectionEnrollments[j].teacher;
				if (teachers.some((e) => e.id === teacher.id)) {
					continue;
				}
				teachers.push(teacher);
			}
		}

		return { homeRoomSections, teachers };
	}, [data]);

	const { loading: loadingMailCredentials, data: mailCredentialsData } = useStudentMailCredentialsQuery({
		variables: { studentID: student.id },
	});
	const mailCredentials = mailCredentialsData?.studentSchoolMailCredentials;

	if (loading || loadingMailCredentials) {
		return (
			<Column justify="center" align="center" style={{ flexGrow: 1 }}>
				Loading...
			</Column>
		);
	}

	return (
		<Column justify="spaced-start" verticalSpacing="1.5rem" style={{ flexGrow: 1 }}>
			<Conditional>
				<If condition={homeRoomSections.length === 0}>
					<EmptyView
						IconComponent={Icon.GraduationCap}
						iconSize={48}
						primaryText="Class Placement"
						secondaryText="Secondary students will receive their placements on the first day of school."
					/>
				</If>
				<Else>
					<Column justify="spaced-start">
						<span className={styles["bold-heading-text"]}>
							Home {toGrammaticalNumber("Room", homeRoomSections.length)}
						</span>

						<Row justify="spaced-start" overflow="wrap" withVerticalSpacing>
							{homeRoomSections.map((homeRoomSection) => (
								<Badge key={homeRoomSection.id} color="#fff" backgroundColor="var(--primary-color)" size="large">
									{HomeRoomSectionFormat.name(homeRoomSection)}
								</Badge>
							))}
						</Row>
					</Column>
					<If condition={teachers.length > 0}>
						<Column justify="spaced-start">
							<span className={styles["bold-heading-text"]}>{toGrammaticalNumber("Teacher", teachers.length)}</span>

							<Grid responsiveTracks={teachersGridTracks} gap="0.75rem">
								{teachers.map((teacher) => (
									<Column key={teacher.id} justify="spaced-start" verticalSpacing="0.25rem">
										<EmployeeComponents.EmployeeIconNameDescription employee={teacher} iconSize={32} />
										<If condition={!!teacher.bio}>
											<div style={{ marginLeft: "2.5rem" }}>
												<EmployeeBioController employee={teacher} />
											</div>
										</If>
									</Column>
								))}
							</Grid>
						</Column>
					</If>
				</Else>
			</Conditional>

			<If condition={!!mailCredentials}>
				<Rule thickness="2px" />

				<Column justify="spaced-start" verticalSpacing="1rem">
					<span className={styles["bold-heading-text"]}>
						{student.person.givenName}'s school GMAIL account login details
					</span>

					<Column justify="spaced-start">
						<Row justify="spaced-start" align="center">
							<span className={styles["mail-credentials__label"]}>Email:</span>
							<span className={styles["mail-credentials__value"]}>{mailCredentials?.email}</span>
							<CopyButton value={mailCredentials?.email ?? ""} />
						</Row>
						<Row justify="spaced-start" align="center">
							<span className={styles["mail-credentials__label"]}>Temporary Password:</span>
							<span className={styles["mail-credentials__value"]}>{mailCredentials?.password}</span>
							<CopyButton value={mailCredentials?.password ?? ""} />
						</Row>
					</Column>
				</Column>
			</If>
		</Column>
	);
}

type EmployeeBioControllerProps = {
	employee: Pick<Employee, "id" | "bio" | "jobTitle"> & EmployeeNameAndImageFragment;
};

function EmployeeBioController({ employee }: EmployeeBioControllerProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Button variant="link" size="small" onClick={openModal}>
				View Bio
			</Button>

			<Modal.If condition={isModalOpen}>
				<Modal onClose={closeModal}>
					<Modal.Header>
						<EmployeeComponents.EmployeeIconNameDescription employee={employee} iconSize={32} />
					</Modal.Header>
					<Modal.Body>
						<FormattedText>{employee.bio}</FormattedText>
					</Modal.Body>
				</Modal>
			</Modal.If>
		</React.Fragment>
	);
}

export type CopyButtonProps = {
	value: TypeOrFunction<string>;
	disabled?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function CopyButton({ value, disabled, className, style }: CopyButtonProps) {
	const { isTooltipOpen, openTooltip, closeTooltip } = Tooltip.useToggle();
	const { setTimeout } = useTimeout();

	const onClick = React.useCallback(() => {
		navigator.clipboard.writeText(evaluateTypeOrFunction(value)).then(() => {
			openTooltip();
			setTimeout(() => {
				closeTooltip();
			}, 750);
		});
	}, [value, openTooltip, closeTooltip, setTimeout]);

	return (
		<Tooltip.Container isControlled isOpen={isTooltipOpen}>
			<Button
				variant="link"
				size="small"
				onClick={onClick}
				disabled={disabled}
				className={className}
				style={{ display: "block", ...style }}
			>
				<Icon.Copy size={16} className="block" />
			</Button>

			<Tooltip>
				<Tooltip.Body>
					<span style={{ color: "var(--success-color)" }}>Copied!</span>
				</Tooltip.Body>
			</Tooltip>
		</Tooltip.Container>
	);
}
